import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Checkbox, Space, Row, Card, Divider, Menu, Dropdown, Tooltip } from 'antd';

import DatasetBulkLoadEntityModal from '../entity/shared/EntityDropdown/DatasetBulkLoadEntityModal';

import { ReactComponent as DownloadOutlined } from '../../images/downloaded-outline.svg';
import { ReactComponent as DeleteOutlined } from '../../images/delete-outline.svg';
import { ReactComponent as UploadFile } from '../../images/upload-file.svg';
import { ReactComponent as MoreAction } from '../../images/more-vert.svg';
import { EntityXlsxExpectedSheetHeader } from '../entity/shared/types';
import { ComponentSpinner } from '../shared/ComponentSpinner';
import { useTranslation } from '../../i18n/useTranslation';

const GREY_COLOR = '#5F767C';
const DANGER_COLOR = '#d96758';

const styles = {
    row: { padding: '12px' },
    title: { margin: 0 },
};

interface BrowseResultActionsProps {
    isAllSelected: boolean;
    isIndeterminate?: boolean;
    selectedItems: string[];
    isCsvExporting: boolean;
    exportCsv: () => void;
    handleSelectAll: (event) => void;
    confirmDatasetDeletion: () => void;
    expectedHeaders: EntityXlsxExpectedSheetHeader[];
    showCheckbox?: boolean;
    canBulkImport?: boolean;
    canExport?: boolean;
    canDelete?: boolean;
    unauthorisedTooltip?: string;
}

export const BrowseDropDown = styled(Dropdown)`
    display: flex;
    align-items: center;
    cursor: pointer;
`;

export const BrowseMenu = styled(Menu)`
    padding: 0px;
    border-radius: 4px;
    min-width: 140px !important;
`;

function BrowseResultActions({
    selectedItems,
    isAllSelected,
    isIndeterminate = false,
    isCsvExporting,
    handleSelectAll,
    confirmDatasetDeletion,
    exportCsv,
    expectedHeaders,
    showCheckbox = true,
    canBulkImport = true,
    canExport = true,
    canDelete = true,
    unauthorisedTooltip,
}: BrowseResultActionsProps) {
    const { t } = useTranslation();
    const [isBulkLoadModalVisible, setIsBulkLoadModalVisible] = useState<boolean>(false);
    const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);

    const handleDropdownVisibleChange = (visible: boolean) => {
        setIsDropdownVisible(visible);
    };

    const exportDisabled = selectedItems.length === 0 || !canExport;
    const deleteDisabled = selectedItems.length === 0 || !canDelete;

    const handleDeleteClick = () => {
        if (deleteDisabled) return;
        confirmDatasetDeletion();
        setIsDropdownVisible(false);
    };

    const handleExportClick = () => {
        if (exportDisabled) return;
        exportCsv();
        setIsDropdownVisible(false);
    };

    const menu = (
        <BrowseMenu
            id="actions-menu"
            role="menu"
            aria-label={t('Actions menu')}
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    setIsDropdownVisible(false);
                }
            }}
        >
            <Tooltip title={!canExport ? unauthorisedTooltip : undefined} overlayClassName="f-tooltip-white">
                <span>
                    <Menu.Item
                        key="1"
                        role="menuitem"
                        tabIndex={0}
                        onClick={handleExportClick}
                        className="f-color-gray-s100"
                        disabled={exportDisabled}
                        icon={<DownloadOutlined style={{ width: 16 }} color="#5F767C" />}
                        aria-disabled={exportDisabled}
                    >
                        {t('Export')}
                    </Menu.Item>
                </span>
            </Tooltip>
            <Divider className="ma-0" />
            <Tooltip title={!canDelete ? unauthorisedTooltip : undefined} overlayClassName="f-tooltip-white">
                <span>
                    <Menu.Item
                        key="2"
                        role="menuitem"
                        tabIndex={0}
                        className="f-color-risk-very-high"
                        disabled={deleteDisabled}
                        icon={<DeleteOutlined color={DANGER_COLOR} style={{ width: 10 }} />}
                        onClick={handleDeleteClick}
                        aria-disabled={deleteDisabled}
                    >
                        {t('Delete Datasets')}
                    </Menu.Item>
                </span>
            </Tooltip>
        </BrowseMenu>
    );

    return (
        <>
            {isCsvExporting && <ComponentSpinner />}
            <Divider className="ma-0" />
            <Card>
                <Row
                    style={styles.row}
                    justify={showCheckbox ? 'space-between' : 'end'}
                    align="middle"
                    className="f-px-4-xs"
                >
                    {showCheckbox && (
                        <Space size="middle" align="center">
                            <Checkbox
                                disabled={!canExport && !canDelete}
                                checked={isAllSelected}
                                indeterminate={isIndeterminate}
                                onChange={handleSelectAll}
                            >
                                {t('Select All')}
                            </Checkbox>
                        </Space>
                    )}
                    <Row align="middle">
                        <Tooltip
                            title={!canBulkImport && unauthorisedTooltip ? unauthorisedTooltip : undefined}
                            overlayClassName="f-tooltip-white"
                        >
                            <span>
                                <Button
                                    type="link"
                                    className="mr-3"
                                    onClick={() => setIsBulkLoadModalVisible(true)}
                                    disabled={!canBulkImport}
                                    icon={<UploadFile style={{ width: 12 }} color={GREY_COLOR} />}
                                >
                                    <span className="f-color-gray-s100">{t('Bulk Import')}</span>
                                </Button>
                            </span>
                        </Tooltip>
                        <BrowseDropDown
                            overlay={menu}
                            trigger={['click']}
                            onVisibleChange={handleDropdownVisibleChange}
                            visible={isDropdownVisible}
                        >
                            <Button
                                type="text"
                                aria-expanded={isDropdownVisible}
                                aria-haspopup="true"
                                aria-controls="actions-menu"
                            >
                                <Row align="middle" style={{ gap: 8 }}>
                                    <MoreAction
                                        data-testid="entity-header-dropdown"
                                        width={12}
                                        height={12}
                                        color={GREY_COLOR}
                                    />
                                    <span className="f-color-gray-s100">{t('Actions')}</span>
                                </Row>
                            </Button>
                        </BrowseDropDown>
                    </Row>
                </Row>
                <Divider className="ma-0" />
            </Card>

            {/* BulkLoad Entity Modal */}
            {isBulkLoadModalVisible && (
                <DatasetBulkLoadEntityModal
                    onClose={() => setIsBulkLoadModalVisible(false)}
                    expectedHeadersFromApi={expectedHeaders}
                />
            )}
        </>
    );
}

export default BrowseResultActions;
